import React, { useEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

const RichTextEditor = ({ value, onChange, placeholder }) => {
  const containerRef = useRef(null);
  const quillRef = useRef(null);
  const isInternalChange = useRef(false);
  const lastValueRef = useRef('');
  const timerRef = useRef(null);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (containerRef.current && !quillRef.current) {
      const quill = new Quill(containerRef.current, {
        modules: {
          toolbar: {
            container: [
              [{ 'header': [1, 2, 3, false] }],
              ['bold', 'italic', 'underline', 'strike'],
              [{ 'list': 'ordered' }, { 'list': 'bullet' }],
              [{ 'color': [] }, { 'background': [] }],
              [{ 'align': [] }],
              ['link', 'image'],
              ['clean']
            ],
            handlers: {
              image: function() {
                const input = document.createElement('input');
                input.setAttribute('type', 'file');
                input.setAttribute('accept', 'image/*');
                input.click();
                input.onchange = async () => {
                  const file = input.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                      const range = this.quill.getSelection(true);
                      this.quill.insertEmbed(range.index, 'image', e.target.result);
                      this.quill.setSelection(range.index + 1);
                    };
                    reader.readAsDataURL(file);
                  }
                };
              }
            }
          },
        },
        formats: [
          'header',
          'bold', 'italic', 'underline', 'strike',
          'list',
          'color', 'background',
          'align',
          'link', 'image'
        ],
        placeholder: placeholder || 'Compose an epic...',
        theme: 'snow'
      });

      quillRef.current = quill;

      const handleTextChange = () => {
        const html = quill.root.innerHTML;
        const normalizedHtml = (html === '<p><br></p>' || html === '<p></p>') ? '' : html;
        
        // Update our local tracking ref
        lastValueRef.current = normalizedHtml;
        
        // Mark as internal change and sync to parent
        isInternalChange.current = true;
        if (onChangeRef.current) {
          onChangeRef.current(normalizedHtml);
        }
        
        // Reset flag after a short delay
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          isInternalChange.current = false;
        }, 150);
      };

      quill.on('text-change', handleTextChange);

      // Clean up on unmount
      return () => {
        quill.off('text-change', handleTextChange);
        if (timerRef.current) clearTimeout(timerRef.current);
      };
    }
  }, [placeholder]);

  // Sync external value to Quill
  useEffect(() => {
    if (quillRef.current && value !== undefined && !isInternalChange.current) {
      const currentContents = quillRef.current.root.innerHTML;
      const normalizedValue = value === '' ? '<p><br></p>' : value;
      
      if (normalizedValue !== currentContents && value !== lastValueRef.current) {
        quillRef.current.root.innerHTML = value || '';
        lastValueRef.current = value;
      }
    }
  }, [value]);

  return (
    <div className="rich-text-editor">
      <div ref={containerRef} />
    </div>
  );
};

export default RichTextEditor;
